// react
import { useContext, useEffect } from 'react';

// types
import { ShortcutAction } from './ShortcutProvider.types';

// utils
import { ShortcutContext } from './ShortcutContext';
import { isValidActivatorKey } from './ShortcutProvider.utils';

export const useCreateShortcutContext = (
  context: string,
  shortcuts: ShortcutAction[],
  dependencies: any[] = []
) => {
  const {
    createShortcutContext,
    disableShortcut,
    enableShortcut,
    register,
    setActiveShortcutContexts,
    unregister,
    shortcuts: shortcutContexts
  } = useContext(ShortcutContext);

  useEffect(() => {
    createShortcutContext?.(context);
    return () => unregister?.(context);
  }, []);

  useEffect(() => {
    shortcuts.forEach(
      ({
        callback,
        name,
        label,
        description,
        activatorKey,
        controllerKeys,
        active
      }) => {
        // Sanity check, should not be possible due to type restrictions
        if (!isValidActivatorKey(activatorKey)) {
          console.error(`Invalid activatorKey used: ${activatorKey}`);
          return;
        }

        register?.({
          context,
          name,
          label,
          description,
          callback,
          activatorKey,
          controllerKeys,
          active
        });
      }
    );
  }, [dependencies]);

  return {
    disableShortcut,
    enableShortcut,
    isActive: shortcutContexts[context]?.isActive,
    setActiveShortcutContexts,
    setCurrentContextActive: () => {
      if (!shortcutContexts[context]) {
        const activeContexts = Object.entries(shortcutContexts)
          .filter(([, context]) => context.isActive)
          .map(([contextName]) => contextName);

        setActiveShortcutContexts?.([...(activeContexts || []), context]);
      }
    },
    setCurrentContextInactive: () => {
      const activeContexts = Object.entries(shortcutContexts)
        .filter(([, context]) => context.isActive)
        .map(([contextName]) => contextName);

      if (activeContexts?.includes(context)) {
        setActiveShortcutContexts?.(
          activeContexts.filter((ctx) => ctx !== context)
        );
      }
    },
    unregister
  };
};
